import type {
  Appointment,
  ChargeItem,
  ChargeItemDefinition,
  Encounter,
  EpisodeOfCare,
  Patient,
  RelatedPerson,
} from '@medplum/fhirtypes';
import type { AppointmentParticipantRow } from '@/components/AppointmentParticipantsTable';
import type { AppointmentServiceCodeRow } from '@/components/AppointmentServiceCodesTable';
import { isCohortGroupAppointment } from '@/lib/groupTherapy';
import { getChargeItemCode, getChargeItemDefinitionUri } from '@unify/fhir-model/resources/charge-item';
import { getChargeItemDefinitionCode } from '@unify/fhir-model/resources/charge-item-definition';
import { resolveAppointmentModality } from '@/pages/appointments/utils/appointmentModality';
import {
  enrichAppointmentAttendeeDisplayNames,
  formatFhirHumanName,
  formatFhirPatientDisplayName,
  getAllClientParticipantsFromAppointment,
  getAllPatientParticipantsFromAppointment,
  getPatientIdFromAppointment,
  type AppointmentAttendee,
} from '@/pages/appointments/utils/fhirHelpers';
import type { ParticipantAttendanceFhirStatus } from '@/pages/appointments/utils/participantAttendance';
import { participantFallbackLabel } from './sessionSchedulerOptions';

export function formatModalityForParticipant(
  modality: ReturnType<typeof resolveAppointmentModality> | undefined,
): string {
  if (!modality) return '';
  if (modality === 'in-person') return 'In person';
  if (modality === 'telehealth') return 'Telehealth';
  return 'Hybrid';
}

export function buildAppointmentServiceCodeRows(input: {
  appointment: Appointment;
  encounter?: Encounter | null;
  chargeItems?: ChargeItem[];
  episodeOfCare?: EpisodeOfCare | null;
  defaultBillingDefinition?: ChargeItemDefinition;
  billingDefinitions?: ChargeItemDefinition[];
}): AppointmentServiceCodeRow[] {
  const chargeItems = input.chargeItems ?? [];
  const definitions = input.billingDefinitions ?? (
    input.defaultBillingDefinition ? [input.defaultBillingDefinition] : []
  );
  const byUrl = new Map(
    definitions.flatMap((def) => def.url ? [[def.url, def] as const] : []),
  );
  const byCode = new Map(
    definitions.flatMap((def) => {
      const code = getChargeItemDefinitionCode(def);
      return code ? [[code, def] as const] : [];
    }),
  );

  const titleFromDefinition = (code: string, definitionUri?: string): string => {
    const matched = (definitionUri ? byUrl.get(definitionUri) : undefined) ?? byCode.get(code);
    return matched?.title?.trim()
      || matched?.code?.coding?.[0]?.display?.trim()
      || '';
  };

  if (chargeItems.length === 0) {
    const definition = input.defaultBillingDefinition;
    const code = definition ? getChargeItemDefinitionCode(definition) : undefined;
    if (!definition?.id || !code) return [];
    return [{
      id: definition.id,
      code,
      description: titleFromDefinition(code, definition.url) || code,
      units: 1,
    }];
  }

  return chargeItems.flatMap((chargeItem, index) => {
    const code = getChargeItemCode(chargeItem);
    if (!code) return [];
    const display = chargeItem.code?.coding?.[0]?.display?.trim();
    const description = titleFromDefinition(code, getChargeItemDefinitionUri(chargeItem))
      || (display && display !== code ? display : '');
    const units = chargeItem.quantity?.value;
    return [{
      id: chargeItem.id ?? `${code}-${index}`,
      code,
      description,
      units: units != null && units > 0 ? units : 1,
    }];
  });
}

export function previewServiceCodeRowsForSessionType(selected: {
  id: string;
  cptCode?: string;
  billingTitle?: string;
} | undefined): AppointmentServiceCodeRow[] {
  const code = selected?.cptCode?.trim();
  if (!selected?.id || !code) return [];
  return [{
    id: selected.id,
    code,
    description: selected.billingTitle?.trim() ?? '',
    units: 1,
  }];
}

export function selectAppointmentTableAttendees(
  appointment: Appointment,
  patientsById: Record<string, Patient | undefined>,
  relatedPersonsById: Record<string, RelatedPerson | undefined>,
): AppointmentAttendee[] {
  const patients = enrichAppointmentAttendeeDisplayNames(
    getAllPatientParticipantsFromAppointment(appointment),
    patientsById,
    relatedPersonsById,
  );
  const clients = enrichAppointmentAttendeeDisplayNames(
    getAllClientParticipantsFromAppointment(appointment),
    patientsById,
    relatedPersonsById,
  );
  const isGroup =
    patients.length > 1 || isCohortGroupAppointment(appointment);
  return isGroup && patients.length > 0 ? patients : clients;
}

export function buildAppointmentParticipantRows(input: {
  appointment: Appointment;
  attendees: AppointmentAttendee[];
  patientsById: Record<string, Patient | undefined>;
  relatedPersonsById: Record<string, RelatedPerson | undefined>;
  linkedPatientsByRelatedPersonId?: Record<string, Patient | undefined>;
  locationByPatientId?: Record<string, string | undefined>;
  attendanceByParticipantId?: Record<string, ParticipantAttendanceFhirStatus | string | undefined>;
  subjectPatientId?: string;
  isCohortAppointment?: boolean;
  modalityLabel?: string;
}): AppointmentParticipantRow[] {
  const modality =
    input.modalityLabel
    ?? formatModalityForParticipant(resolveAppointmentModality(input.appointment));
  const subjectPatientId = input.subjectPatientId ?? getPatientIdFromAppointment(input.appointment);
  const isCohort = input.isCohortAppointment ?? isCohortGroupAppointment(input.appointment);

  return input.attendees.map((a, index) => {
    const ref = a.actorReference?.trim();
    const patientId = ref?.startsWith('Patient/')
      ? ref.slice('Patient/'.length)
      : undefined;
    const relatedPersonId = ref?.startsWith('RelatedPerson/')
      ? ref.slice('RelatedPerson/'.length)
      : undefined;
    const participantId = patientId ?? relatedPersonId ?? `participant-${index}`;
    const loadedPatient = patientId ? input.patientsById[patientId] : undefined;
    const loadedRelatedPerson = relatedPersonId
      ? input.relatedPersonsById[relatedPersonId]
      : undefined;
    const linkedPatient = relatedPersonId
      ? input.linkedPatientsByRelatedPersonId?.[relatedPersonId]
      : undefined;
    const contactPatient = loadedPatient ?? linkedPatient;
    const telecom = contactPatient?.telecom ?? loadedRelatedPerson?.telecom;
    const email = telecom?.find((t) => t.system === 'email')?.value;
    const phone = telecom?.find((t) => t.system === 'phone')?.value;
    const linkedPatientId = linkedPatient?.id;
    return {
      id: participantId,
      participantRef: ref,
      name: formatFhirPatientDisplayName(contactPatient) ?? a.displayName,
      profileHref:
        a.profileHref
        ?? (patientId ? `/dashboard/clients/${patientId}` : undefined)
        ?? (linkedPatientId ? `/dashboard/clients/${linkedPatientId}` : undefined),
      email,
      phone,
      modality,
      location: participantId ? input.locationByPatientId?.[participantId] : undefined,
      attendanceStatus: participantId
        ? input.attendanceByParticipantId?.[participantId]
        : undefined,
      isSubject: !isCohort && Boolean(subjectPatientId && patientId === subjectPatientId),
      role: relatedPersonId ? (linkedPatientId ? 'Client' : 'Participant') : 'Client',
    };
  });
}

export function buildEditableAppointmentParticipantRows(input: {
  selectedRefs: string[];
  options: { value: string; label: string }[];
  existingRows?: AppointmentParticipantRow[];
  subjectPatientId?: string;
  modality?: 'in-person' | 'telehealth' | 'hybrid';
  relatedPersonsById?: Record<string, RelatedPerson | undefined>;
  patientsById?: Record<string, Patient | undefined>;
}): AppointmentParticipantRow[] {
  const existingByRef = new Map<string, AppointmentParticipantRow>(
    (input.existingRows ?? [])
      .filter((row): row is AppointmentParticipantRow & { participantRef: string } =>
        Boolean(row.participantRef),
      )
      .map((row) => [row.participantRef, row]),
  );
  const labelByRef = new Map(input.options.map((option) => [option.value, option.label]));
  const modality = input.modality
    ? formatModalityForParticipant(input.modality)
    : undefined;

  return input.selectedRefs.map((ref, index) => {
    const separator = ref.indexOf('/');
    const id = separator >= 0 ? ref.slice(separator + 1) : ref;
    const patientId = ref.startsWith('Patient/') ? id : undefined;
    const relatedPersonId = ref.startsWith('RelatedPerson/') ? id : undefined;
    const relatedPerson = relatedPersonId
      ? input.relatedPersonsById?.[relatedPersonId]
      : undefined;
    const patient = patientId ? input.patientsById?.[patientId] : undefined;
    const telecom = patient?.telecom ?? relatedPerson?.telecom;
    const emailFromResource = telecom?.find((t) => t.system === 'email')?.value;
    const phoneFromResource = telecom?.find((t) => t.system === 'phone')?.value;
    const nameFromResource =
      formatFhirHumanName(relatedPerson?.name?.[0])
      ?? formatFhirPatientDisplayName(patient);

    const existing = existingByRef.get(ref);
    if (existing) {
      return {
        ...existing,
        ...(modality ? { modality } : {}),
        name: nameFromResource || existing.name,
        profileHref:
          existing.profileHref
          ?? (patientId ? `/dashboard/clients/${patientId}` : undefined),
        email: existing.email || emailFromResource,
        phone: existing.phone || phoneFromResource,
      };
    }

    const label = labelByRef.get(ref);
    return {
      id: id || `participant-${index}`,
      participantRef: ref,
      name:
        nameFromResource
        || (label && label !== ref ? label : participantFallbackLabel(id)),
      profileHref: patientId ? `/dashboard/clients/${patientId}` : undefined,
      email: emailFromResource,
      phone: phoneFromResource,
      modality,
      isSubject: Boolean(patientId && patientId === input.subjectPatientId),
      role: ref.startsWith('RelatedPerson/') ? 'Participant' : 'Client',
    };
  });
}

export function withParticipantRowModality(
  rows: AppointmentParticipantRow[],
  modality: 'in-person' | 'telehealth' | 'hybrid' | undefined,
): AppointmentParticipantRow[] {
  if (!modality) return rows;
  const label = formatModalityForParticipant(modality);
  return rows.map((row) => ({ ...row, modality: label }));
}
